import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Box, Button, ButtonGroup, GridLegacy as Grid, Typography } from '@mui/material';

import ReservationService from '~/services/ReservationService';

import AmenityGroupsSortableItem from './AmenityGroupsSortableItem';

type Props = {
  data: Array<App.AmenityGroup>;
};

type SortableAmenityGroup = App.AmenityGroup & { checked: boolean };

export default function AmenityGroupSearchResults({ data }: Props) {
  const [groups, setGroups] = useState<Array<SortableAmenityGroup>>(
    data.map((group) => ({ ...group, checked: false })),
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [saveButtonMessage, setSaveButtonMessage] = useState('No changes');

  // Update groups when data prop changes (e.g., after adding a new group)
  useEffect(() => {
    setGroups(data.map((group) => ({ ...group, checked: false })));
  }, [data]);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const getEditUrl = (id: string) => {
    return '/amenity-groups/' + id;
  };

  const getAmenities = (amenities: Array<App.Amenity>) => {
    const amenitiesList = amenities.map((amenity) => amenity.name);
    return amenitiesList.join(' | ');
  };

  const recalculateListOrder = (groups: Array<SortableAmenityGroup>) => {
    let order = 1;
    for (const i in groups) {
      groups[i].order = order++;
    }
    return groups;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = groups.findIndex((group) => group.id === active.id);
      const newIndex = groups.findIndex((group) => group.id === over?.id);

      let newGroupList = arrayMove(groups, oldIndex, newIndex);
      newGroupList = recalculateListOrder(newGroupList);

      setGroups(newGroupList);
      setHasChanges(true);
      setSaveButtonMessage('Save Changes');
    }
  };

  const onGroupToggle = useCallback(
    (index: number) => () => {
      const newGroups = [...groups];
      newGroups[index].checked = !newGroups[index].checked;
      setGroups(newGroups);
    },
    [groups],
  );

  const calculateDelta = (oldData: Array<App.AmenityGroup>, changedData: Array<App.AmenityGroup>) => {
    const delta = [];
    for (const i in oldData) {
      if (oldData[i].id !== changedData[i].id) {
        delta.push(changedData[i]);
      }
    }
    return delta;
  };

  const saveChanges = useCallback(() => {
    const delta = calculateDelta(data, groups);
    ReservationService.updateAmenityGroups(delta);
    setHasChanges(false);
    setSaveButtonMessage('No changes');
  }, [data, groups]);

  const onPushToTopOrBottom = useCallback(
    (pushDirection: string) => () => {
      const checkedGroups = groups.filter((offer) => offer.checked);

      if (checkedGroups.length === 0) {
        return;
      }

      const uncheckedGroups = groups.filter((offer) => !offer.checked);

      for (const offer of checkedGroups) {
        offer.checked = false;
      }

      let newGroups = [];
      switch (pushDirection) {
        case 'top':
          newGroups = checkedGroups.concat(uncheckedGroups);
          break;
        case 'bottom':
          newGroups = uncheckedGroups.concat(checkedGroups);
      }

      newGroups = recalculateListOrder(newGroups);
      setHasChanges(true);
      setSaveButtonMessage('Save Changes');
      setGroups(newGroups);
    },
    [groups],
  );

  const pushingDisabled: boolean = useMemo(() => {
    return groups.filter((group) => group.checked).length === 0;
  }, [groups]);

  return (
    <Box className="group-list-order-page T-Group-List-Order">
      <Grid container spacing={2}>
        <Grid item md={10}>
          <Typography variant="body1">Click and drag to re-order, then click Save to confirm the changes.</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={10}>
          <ButtonGroup orientation="horizontal" className="group-default-button-group">
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={pushingDisabled}
              className={'group-default-button ' + (pushingDisabled ? 'group-default-button-disabled' : '')}
              onClick={onPushToTopOrBottom('top')}
            >
              Push to top
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={pushingDisabled}
              className={'group-default-button ' + (pushingDisabled ? 'group-default-button-disabled' : '')}
              onClick={onPushToTopOrBottom('bottom')}
            >
              Push to bottom
            </Button>
          </ButtonGroup>
        </Grid>
        <Grid item md={2}>
          <Button variant="contained" fullWidth color="primary" disabled={!hasChanges} onClick={saveChanges}>
            {saveButtonMessage}
          </Button>
        </Grid>
      </Grid>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={groups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
          <Box component="ul" className="sortable-amenity-group-list">
            {groups.map((group, index) => (
              <AmenityGroupsSortableItem
                key={group.id}
                value={{ index, group }}
                onGroupToggle={onGroupToggle}
                getAmenities={getAmenities}
                getEditUrl={getEditUrl}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
